'use client';

// AppHeader.tsx
import { RefObject, useCallback, useSyncExternalStore } from 'react';
import { useRouter } from 'next/navigation';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import {
  AuthButtons,
  DepositButton,
  HamburgerMenu,
  HowItWorksButton,
  LanguageSelector,
  Logo,
  NotificationBell,
  ReferralButton,
  SearchBar,
  UserSettingsMenu,
} from '@/components';
import { HeaderActionsSkeleton } from '../../skeletons';
import { PortfolioBalances } from './PortfolioBalances';

import { ROUTES } from '@/constants';

interface AppHeaderActionsProps {
  isAuthenticated: boolean;
  isLoading?: boolean;
  portfolioValue: number;
  cashBalance: number;
  isLoadingPortfolio?: boolean;
  onHowItWorksOpen: () => void;
  onLoginClick: () => void;
  onDepositModalOpen: () => void;
  onReferralModalOpen: () => void;
  isReferredUser?: boolean;
  isAuthSyncing?: boolean;
}

interface AppHeaderProps {
  searchInputRef: RefObject<HTMLDivElement>;
  isAuthenticated: boolean;
  isLoading?: boolean;
  portfolioValue: number;
  cashBalance: number;
  isLoadingPortfolio?: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSearchClick: (e: React.MouseEvent | React.FocusEvent) => void;
  onSearchSubmit: (value: string) => void;
  onHowItWorksOpen: () => void;
  onLoginClick: () => void;
  onDepositModalOpen: () => void;
  onReferralModalOpen: () => void;
  isReferredUser?: boolean;
  isAuthSyncing?: boolean;
}

const GuestActions = ({
  onHowItWorksOpen,
  onLoginClick,
  isAuthSyncing,
}: Pick<AppHeaderActionsProps, 'onHowItWorksOpen' | 'onLoginClick' | 'isAuthSyncing'>) => {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
      <HowItWorksButton onClick={onHowItWorksOpen} />
      <AuthButtons onLoginClick={onLoginClick} loading={isAuthSyncing} />
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <HamburgerMenu />
      </Box>
    </Box>
  );
};

const AuthenticatedActions = ({
  portfolioValue,
  cashBalance,
  isLoadingPortfolio,
  onDepositModalOpen,
  onReferralModalOpen,
  isReferredUser,
}: Pick<
  AppHeaderActionsProps,
  | 'portfolioValue'
  | 'cashBalance'
  | 'isLoadingPortfolio'
  | 'onDepositModalOpen'
  | 'onReferralModalOpen'
  | 'isReferredUser'
>) => {
  const router = useRouter();

  const handleReferralClick = () => {
    if (isReferredUser) {
      router.push(ROUTES.REFERRALS);
    } else {
      onReferralModalOpen();
    }
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
      <DepositButton onClick={onDepositModalOpen} />
      <ReferralButton onClick={handleReferralClick} />
      <PortfolioBalances
        portfolioValue={portfolioValue}
        cashBalance={cashBalance}
        isLoading={isLoadingPortfolio}
      />
      <NotificationBell />
      <UserSettingsMenu />
    </Box>
  );
};

const HeaderActions = (props: AppHeaderActionsProps) => {
  const { isAuthenticated, isLoading, isAuthSyncing } = props;
  // Show skeleton during the very first load or if authenticated state is truly unknown
  if (isLoading && isAuthenticated) {
    return <HeaderActionsSkeleton />;
  }

  if (isAuthenticated) {
    return <AuthenticatedActions {...props} />;
  }

  return <GuestActions {...props} isAuthSyncing={isAuthSyncing || isLoading} />;
};

export const AppHeader = ({
  searchInputRef,
  isAuthenticated,
  portfolioValue,
  cashBalance,
  isLoadingPortfolio,
  searchQuery,
  onSearchChange,
  onSearchClick,
  onSearchSubmit,
  onHowItWorksOpen,
  onLoginClick,
  onDepositModalOpen,
  onReferralModalOpen,
  isReferredUser,
  isLoading,
  isAuthSyncing,
}: AppHeaderProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const mounted = useSyncExternalStore(
    useCallback(() => () => {}, []),
    () => true,
    () => false
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: 0.75, sm: 1, md: 2 },
        px: { xs: 1.5, sm: 2, md: 3, lg: 4 },
        py: 1,
        minHeight: { xs: 56, sm: 60 },
        width: '100%',
        maxWidth: '100%',
        flexWrap: 'nowrap',
      }}
    >
      <Logo />
      {mounted && isDesktop && (
        <SearchBar
          searchInputRef={searchInputRef}
          onSearchClick={onSearchClick}
          value={searchQuery}
          onChange={onSearchChange}
          onSubmit={onSearchSubmit}
        />
      )}
      {mounted && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
          <LanguageSelector compact />
          <HeaderActions
            isAuthenticated={isAuthenticated}
            isLoading={isLoading}
            portfolioValue={portfolioValue}
            cashBalance={cashBalance}
            isLoadingPortfolio={isLoadingPortfolio}
            onHowItWorksOpen={onHowItWorksOpen}
            onLoginClick={onLoginClick}
            onDepositModalOpen={onDepositModalOpen}
            onReferralModalOpen={onReferralModalOpen}
            isReferredUser={isReferredUser}
            isAuthSyncing={isAuthSyncing}
          />
        </Box>
      )}
    </Box>
  );
};
